import { Box } from '@chakra-ui/react';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
// eslint-disable-next-line import/no-cycle
import { Shadows } from '../ChakraTheme';
import { Border, BorderWidth } from '../ChakraTheme/configs/Borders.config';
import {
    ChakraColors,
    Colors,
    ColorSchemes,
} from '../ChakraTheme/configs/Colors.config';
import { Spacing } from '../ChakraTheme/configs/Spacing.config';
import { CColumn, CRow } from '../Layouts';
import { CardBaseProps } from './Cards.type';
import { ButtonBaseProps, CMonochromaticButton } from '../Buttons';
import { CSubHeader, CText } from '../Typography';
import { CFAIcon } from '../Icons';
import { useChakraTheme } from '../ChakraTheme/useChakraTheme.hooks';

const CCardBase = ({ children, ...props }: CardBaseProps): JSX.Element => (
    <Box
        borderRadius={Border.Radius.SM}
        borderStyle="solid"
        p={Spacing.SM}
        wordBreak="break-word"
        {...props}
    >
        {children}
    </Box>
);

export const CCardHeader = ({
    children,
    ...props
}: CardBaseProps): JSX.Element => (
    <CRow
        alignItems="center"
        borderBottom={`${BorderWidth.Thin} solid`}
        borderBottomColor={Colors.grayLightest}
        pb={Spacing.SM}
        mb={Spacing.SM}
        {...props}
    >
        {children}
    </CRow>
);

export const CGrayCard = ({
    children,
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase {...props} bgColor={Colors.cardGray}>
        {children}
    </CCardBase>
);

export const CWhiteCard = ({
    children,
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase
        p={Spacing.MD}
        bgColor={Colors.white}
        borderColor={Colors.grayLight}
        borderWidth={BorderWidth.Thinnest}
        {...props}
    >
        {children}
    </CCardBase>
);

export const CKpiCard = ({
    children,
    color = 'red',
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase
        p={Spacing.MD}
        bgColor={Colors.white}
        borderColor={Colors.grayLight}
        borderWidth={BorderWidth.Thinnest}
        borderBottomColor={color}
        borderBottomWidth={5}
        borderRadius={Border.Radius.MD}
        {...props}
    >
        {children}
    </CCardBase>
);

export const CShadowedCard = ({
    children,
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase
        p={Spacing.MD}
        bgColor={Colors.white}
        borderColor="transparent"
        borderWidth={BorderWidth.Thinnest}
        boxShadow={Shadows.BASE}
        {...props}
    >
        {children}
    </CCardBase>
);

export const CBorderedCard = ({
    children,
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase
        {...props}
        border={Border.Width.Thin}
        borderColor={Colors.primary}
        bg={Colors.white}
    >
        {children}
    </CCardBase>
);

export const CBorderedGrayCard = ({
    children,
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase
        {...props}
        border={Border.Width.Thin}
        borderColor={Colors.grayLight}
        bgColor={Colors.grayLightest}
    >
        {children}
    </CCardBase>
);

// TODO:  come back to cards and convert border and white card to base variants

export const CBorderedSuccessCard = ({
    children,
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase
        {...props}
        p={Spacing.SM}
        border={Border.Width.Thin}
        borderColor={Colors.success}
    >
        {children}
    </CCardBase>
);

type SlotPosition = 'left' | 'right';
type Slots = Partial<Record<SlotPosition, JSX.Element>>;

const SlotStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexBasis: '200px',
};

export const CSlottedCard = ({
    slots,
    children,
    ...props
}: CardBaseProps & {
    slots: Slots;
}): JSX.Element => {
    const hasSlots = Object.keys(slots).length > 0;

    return (
        <CCardBase
            {...props}
            w="100%"
            p={0}
            border={BorderWidth.Thinnest}
            borderColor={Colors.grayLight}
            bg={Colors.white}
            display="flex"
            boxShadow={Shadows.SMALL}
            overflow="hidden"
        >
            {slots.left && (
                <Box {...SlotStyles} bg={Colors.grayLightest}>
                    {slots.left}
                </Box>
            )}

            <Box
                flexGrow={1}
                flexShrink={1}
                w="100%"
                h="100%"
                minWidth={0}
                pl={hasSlots ? Spacing.SM : 0}
            >
                {children}
            </Box>

            {slots.right && <Box {...SlotStyles}>{slots.right}</Box>}
        </CCardBase>
    );
};

export const CBottomAccentBorderedCard = ({
    children,
    borderBottomColor,
    ...props
}: CardBaseProps): JSX.Element => (
    <CCardBase
        {...props}
        border={Border.Width.Thin}
        borderColor={Colors.grayLight}
        borderBottomColor={borderBottomColor}
        bgColor={Colors.white}
    >
        {children}
    </CCardBase>
);

export const CMonoChromaticCard = ({
    buttonProps,
    color,
    icon,
    description,
    headerText,
    ...props
}: Omit<CardBaseProps, 'children'> & {
    color: ColorSchemes;
    description: string;
    headerText: string;
    buttonProps?: ButtonBaseProps;
    icon?: IconDefinition;
}): JSX.Element => {
    const theme = useChakraTheme();
    const colorMap =
        theme.colors[color as keyof ChakraColors] ?? theme.colors.gray;
    return (
        <CCardBase
            p={Spacing.MD}
            width="100%"
            // come back to this
            bgColor={colorMap['100']}
            {...props}
        >
            <CRow noBreak height="100%">
                {icon && (
                    <CFAIcon
                        icon={icon}
                        color={colorMap['400']}
                        width="32px"
                        height="32px"
                    />
                )}
                <CColumn
                    height="100%"
                    gap={Spacing.SM}
                    flexWrap="nowrap"
                    alignContent="stretch"
                >
                    <CSubHeader
                        color={colorMap['500']}
                        text={headerText}
                        mb="0"
                        lineHeight="32px"
                    />
                    <Box flex="1 1 auto">
                        <CText text={description} />
                    </Box>
                    {buttonProps && (
                        <CMonochromaticButton {...buttonProps} color={color} />
                    )}
                </CColumn>
            </CRow>
        </CCardBase>
    );
};
